import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs, limit } from 'firebase/firestore';

function GrowthSpeed({ userId, profile, onClose, guestMode }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecords();
  }, [userId]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      if (guestMode) {
        const guestRecords = JSON.parse(localStorage.getItem('guestRecords') || '[]');
        guestRecords.sort((a, b) => new Date(b.date) - new Date(a.date));
        setRecords(guestRecords);
      } else {
        const recordsRef = collection(db, 'users', userId, 'records');
        const q = query(recordsRef, orderBy('date', 'desc'), limit(10));
        const querySnapshot = await getDocs(q);

        const recordsData = [];
        querySnapshot.forEach((doc) => {
          recordsData.push({ id: doc.id, ...doc.data() });
        });

        setRecords(recordsData);
      }
    } catch (error) {
      console.error('기록 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  // 성장 속도 계산
  const calculateGrowthSpeed = () => {
    if (records.length < 2) return null;

    const latest = records[0];
    const oldest = records[records.length - 1];

    const latestDate = new Date(latest.date);
    const oldestDate = new Date(oldest.date);
    const daysDiff = (latestDate - oldestDate) / (1000 * 60 * 60 * 24);
    const monthsDiff = daysDiff / 30.44;

    if (monthsDiff < 0.1) return null;

    const heightDiff = latest.height - oldest.height;
    const weightDiff = latest.weight - oldest.weight;

    const heightPerMonth = heightDiff / monthsDiff;
    const weightPerMonth = weightDiff / monthsDiff;

    return {
      heightPerMonth,
      weightPerMonth,
      period: monthsDiff,
      totalHeightGrowth: heightDiff,
      totalWeightGrowth: weightDiff
    };
  };

  // 평균 성장 속도 (월별)
  const getAverageGrowthSpeed = (ageInMonths) => {
    // 0-3개월: 빠른 성장
    if (ageInMonths < 3) return { height: 3.5, weight: 0.8 };
    // 3-6개월: 중간 성장
    if (ageInMonths < 6) return { height: 2.0, weight: 0.5 };
    // 6-12개월: 느려지는 성장
    if (ageInMonths < 12) return { height: 1.2, weight: 0.3 };
    // 12-24개월: 더 느린 성장
    if (ageInMonths < 24) return { height: 1.0, weight: 0.25 };
    // 24개월 이상
    return { height: 0.8, weight: 0.2 };
  };

  const calculateAge = (birthDate) => {
    const birth = new Date(birthDate);
    const today = new Date();
    return (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
  };

  const speed = calculateGrowthSpeed();
  const babyAge = calculateAge(profile.birthDate);
  const averageSpeed = getAverageGrowthSpeed(babyAge);

  // 성장 속도 비교
  const getSpeedComparison = (actual, average) => {
    const ratio = actual / average;
    if (ratio > 1.5) return { text: '매우 빠름', emoji: '🚀', color: '#66BB6A' };
    if (ratio > 1.2) return { text: '빠름', emoji: '⚡', color: '#81C784' };
    if (ratio > 0.8) return { text: '평균', emoji: '✅', color: '#E8A87C' };
    if (ratio > 0.5) return { text: '느림', emoji: '🐢', color: '#FFB74D' };
    return { text: '매우 느림', emoji: '⚠️', color: '#E57373' };
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '24px',
          padding: '40px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <p style={{ color: '#8B7355' }}>분석 중...</p>
        </div>
      </div>
    );
  }

  if (!speed || records.length < 2) {
    return (
      <div className="fade-in" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
        <div className="slide-up" style={{
          backgroundColor: 'white',
          borderRadius: '24px',
          padding: '40px',
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center',
          position: 'relative'
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#8B7355'
            }}
          >
            ×
          </button>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
          <h3 style={{ color: '#E8A87C', marginBottom: '12px' }}>기록이 부족해요</h3>
          <p style={{ color: '#8B7355', fontSize: '14px', lineHeight: '1.6' }}>
            성장 속도를 분석하려면<br />최소 2개 이상의 기록이 필요합니다.
          </p>
          <button
            onClick={onClose}
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              backgroundColor: '#E8A87C',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            닫기
          </button>
        </div>
      </div>
    );
  }

  const heightComparison = getSpeedComparison(speed.heightPerMonth, averageSpeed.height);
  const weightComparison = getSpeedComparison(speed.weightPerMonth, averageSpeed.weight);

  return (
    <div className="fade-in" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div className="slide-up" style={{
        backgroundColor: 'white',
        borderRadius: '24px',
        padding: '40px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#8B7355'
          }}
        >
          ×
        </button>

        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚀</div>
          <h2 style={{ color: '#E8A87C', fontSize: '24px', marginBottom: '8px' }}>
            성장 속도 분석
          </h2>
          <p style={{ color: '#8B7355', fontSize: '14px' }}>
            최근 {speed.period.toFixed(1)}개월간의 성장 속도
          </p>
        </div>

        {/* 키 성장 속도 */}
        <div style={{
          backgroundColor: '#E8F5E9',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '16px',
          border: `2px solid ${heightComparison.color}`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '24px' }}>📏</span>
              <h3 style={{ color: heightComparison.color, margin: 0, fontSize: '18px' }}>키 성장</h3>
            </div>
            <div style={{
              backgroundColor: heightComparison.color,
              color: 'white',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: 'bold'
            }}>
              {heightComparison.emoji} {heightComparison.text}
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '11px', color: '#8B7355', marginBottom: '4px' }}>월평균 성장</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: heightComparison.color }}>
                {speed.heightPerMonth.toFixed(1)}
              </div>
              <div style={{ fontSize: '11px', color: '#8B7355' }}>cm/월</div>
            </div>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '11px', color: '#8B7355', marginBottom: '4px' }}>총 성장</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: heightComparison.color }}>
                {speed.totalHeightGrowth.toFixed(1)}
              </div>
              <div style={{ fontSize: '11px', color: '#8B7355' }}>cm</div>
            </div>
          </div>

          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '12px',
            fontSize: '12px',
            color: '#8B7355',
            textAlign: 'center'
          }}>
            평균 성장 속도: {averageSpeed.height}cm/월<br />
            우리 아기는 평균의 <strong style={{ color: heightComparison.color }}>
              {(speed.heightPerMonth / averageSpeed.height * 100).toFixed(0)}%
            </strong>
          </div>
        </div>

        {/* 몸무게 성장 속도 */}
        <div style={{
          backgroundColor: '#FFF3E0',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '20px',
          border: `2px solid ${weightComparison.color}`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '24px' }}>⚖️</span>
              <h3 style={{ color: weightComparison.color, margin: 0, fontSize: '18px' }}>몸무게 증가</h3>
            </div>
            <div style={{
              backgroundColor: weightComparison.color,
              color: 'white',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: 'bold'
            }}>
              {weightComparison.emoji} {weightComparison.text}
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '11px', color: '#8B7355', marginBottom: '4px' }}>월평균 증가</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: weightComparison.color }}>
                {speed.weightPerMonth.toFixed(2)}
              </div>
              <div style={{ fontSize: '11px', color: '#8B7355' }}>kg/월</div>
            </div>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '11px', color: '#8B7355', marginBottom: '4px' }}>총 증가</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: weightComparison.color }}>
                {speed.totalWeightGrowth.toFixed(2)}
              </div>
              <div style={{ fontSize: '11px', color: '#8B7355' }}>kg</div>
            </div>
          </div>

          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '12px',
            fontSize: '12px',
            color: '#8B7355',
            textAlign: 'center'
          }}>
            평균 증가 속도: {averageSpeed.weight}kg/월<br />
            우리 아기는 평균의 <strong style={{ color: weightComparison.color }}>
              {(speed.weightPerMonth / averageSpeed.weight * 100).toFixed(0)}%
            </strong>
          </div>
        </div>

        <div style={{
          backgroundColor: '#F5E6D3',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <p style={{
            color: '#8B7355',
            fontSize: '12px',
            margin: 0,
            lineHeight: '1.6'
          }}>
            💡 <strong>참고사항</strong><br />
            • 성장 속도는 시기마다 다릅니다<br />
            • 생후 0-3개월은 가장 빠르게 성장해요<br />
            • 일시적으로 느려졌다가 다시 빨라질 수 있어요<br />
            • 꾸준한 성장이 가장 중요합니다
          </p>
        </div>

        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '16px',
            backgroundColor: '#E8A87C',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#D4956B'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#E8A87C'}
        >
          닫기
        </button>
      </div>
    </div>
  );
}

export default GrowthSpeed;
